/**
 * ReconciliationHeader.js
 * Modern, clean header component with enhanced tabs and professional design
 */

import {
  Box,
  Container,
  Typography,
  Stack,
  LinearProgress,
  alpha,
  useTheme,
  Tabs,
  Tab,
  Chip,
  useMediaQuery,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  SwapHoriz as ReconcileIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';

const ReconciliationHeader = ({
  isUploading,
  uploadProgress,
  stats,
  activeTab,
  onTabChange,
  statements,
  selectedStatement,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Safely get statements count
  const getStatementsCount = () => {
    if (Array.isArray(statements)) {
      return statements.length;
    } else if (statements && typeof statements === 'object' && Array.isArray(statements.data)) {
      return statements.data.length;
    }
    return 0;
  };

  const statementsCount = getStatementsCount();

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        borderRadius: 0,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 80%, ${alpha(
            theme.palette.secondary.main,
            0.1
          )} 0%, transparent 50%), radial-gradient(circle at 80% 20%, ${alpha(
            theme.palette.secondary.light,
            0.1
          )} 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Upload Progress Overlay */}
      {isUploading && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: alpha(theme.palette.common.white, 0.1),
          }}
        >
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: '100%',
              backgroundColor: 'transparent',
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
              },
            }}
          />
        </Box>
      )}

      <Container
        maxWidth={false}
        disableGutters
        sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 1, sm: 1.5 } }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 1, md: 2 }}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Header Content */}
          <Stack direction="row" spacing={{ xs: 1, md: 1.5 }} alignItems="center">
            <Box
              sx={{
                p: { xs: 0.5, sm: 0.75 },
                borderRadius: 2,
                background: alpha(theme.palette.common.white, 0.15),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.common.white, 0.25),
                  transform: 'scale(1.05)',
                },
              }}
            >
              <BankIcon sx={{ fontSize: { xs: 18, sm: 22, md: 24 } }} />
            </Box>
            <Box>
              <Typography
                variant={isMobile ? 'subtitle1' : 'h6'}
                fontWeight="700"
                sx={{
                  lineHeight: 1.1,
                  background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${alpha(
                    theme.palette.common.white,
                    0.8
                  )} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0,
                }}
              >
                Conciliação Bancária
              </Typography>

            </Box>
          </Stack>

          {/* Navigation Tabs */}
          <Tabs
            value={activeTab}
            onChange={onTabChange}
            sx={{
              minHeight: { xs: 32, sm: 36 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 600,
                py: { xs: 0.5, sm: 0.75 },
                px: { xs: 1, sm: 1.5 },
                minHeight: { xs: 32, sm: 36 },
                borderRadius: 2,
                margin: '0px 2px',
                color: alpha(theme.palette.common.white, 0.8),
                minWidth: 'auto',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.common.white,
                    0.05
                  )} 0%, ${alpha(theme.palette.common.white, 0.02)} 100%)`,
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                  color: 'white',
                  transform: 'translateY(-1px)',
                  '&::before': { opacity: 1 },
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.common.white, 0.15),
                  color: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.common.white, 0.2)}`,
                  '&::before': { opacity: 1 },
                },
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab
              icon={<DocumentIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    fontWeight="600"
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                  >
                    Upload
                  </Typography>
                  {statementsCount > 0 && (
                    <Chip
                      label={statementsCount}
                      size="small"
                      sx={{
                        height: { xs: 12, sm: 14 },
                        fontSize: { xs: '0.55rem', sm: '0.6rem' },
                        fontWeight: 600,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                        color: 'white',
                        borderRadius: 1,
                        px: 0.25,
                      }}
                    />
                  )}
                </Box>
              }
              iconPosition="start"
            />

            <Tab
              icon={<ReconcileIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    fontWeight="600"
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                  >
                    Conciliação
                  </Typography>
                  {selectedStatement && (
                    <Box
                      sx={{
                        width: { xs: 4, sm: 5 },
                        height: { xs: 4, sm: 5 },
                        borderRadius: '50%',
                        backgroundColor: theme.palette.success.main,
                        boxShadow: `0 0 4px ${theme.palette.success.main}`,
                      }}
                    />
                  )}
                </Box>
              }
              iconPosition="start"
            />
          </Tabs>
        </Stack>
      </Container>
    </Box>
  );
};

export default ReconciliationHeader;
